/** 系统配置/RBAC相关类型 */

/** 配置类型 */
export enum ConfigType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  JSON = 'JSON',
}

/** 系统配置 */
export interface SystemConfig {
  id: string;
  key: string;
  value: string;
  type: ConfigType;
  group: string;
  description: string;
  updatedBy: string;
  updatedAt: string;
}

/** RBAC角色 */
export interface RBACRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  permissionCount: number;
  userCount: number;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

// ===== 真实 RBAC API 类型（对接 blu-user 后端 /system/*） =====

/** 菜单节点（对齐 SysMenu VO，用于动态菜单树 / 权限树） */
export interface MenuNode {
  id: string;
  menuName: string;
  parentId: string | null;
  path: string;
  component: string;
  menuType: 'M' | 'C' | 'F';
  icon: string;
  visible: number;
  status: number;
  orderNum?: number;
  perms?: string;
  children?: MenuNode[];
}

/** 用户（对齐 UserVO / SysUser） */
export interface User {
  id: string;
  username: string;
  realName?: string;
  email?: string;
  phone?: string;
  orgId?: string;
  avatar?: string;
  sex?: string;
  status?: number;
  roles?: string[];
  loginIp?: string;
  loginDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** 角色（对齐 SysRole） */
export interface Role {
  id: string;
  roleName: string;
  roleKey: string;
  roleSort?: number;
  dataScope?: string;
  status?: number;
  remark?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** 组织 / 租户（对齐 SysOrg） */
export interface Org {
  id: string;
  orgCode: string;
  orgName: string;
  parentId: string | null;
  orderNum?: number;
  leader?: string;
  phone?: string;
  email?: string;
  status?: number;
  remark?: string;
  children?: Org[];
}

/** 登录返回（对齐 SysUserController.login） */
export interface LoginResult {
  token: string;
  userId: string;
  username: string;
  orgId: string;
}

/** /info 返回（对齐 SysUserController.getUserInfo） */
export interface UserInfoResult {
  user: User;
  roles: string[];
  permissions: string[];
  menus: MenuNode[];
  /** 是否平台超管（JWT platform 声明） */
  platform?: boolean;
  /** 是否平台超管（与 platform 同义，前端语义化字段） */
  isSuperAdmin?: boolean;
}

/** 分页结果（对齐 MyBatis-Plus IPage 序列化：records/total/size/current/pages） */
export interface PageResult<T> {
  records: T[];
  total: number;
  size: number;
  current: number;
  pages: number;
}

/** 角色下拉选项（对齐 SysRole，用于用户/权限表单） */
export interface RoleOption {
  id: string;
  roleName: string;
  roleKey: string;
  status?: number;
}

/** 审计日志（对应 audit_logs 表） */
export interface AuditLog {
  id: string;
  orgId: string;
  userId: string;
  username: string;
  action: string;
  module: string;
  resourceType: string;
  resourceId: string;
  ip: string;
  requestId: string;
  beforeData: string;
  afterData: string;
  result: 'success' | 'failure';
  errorMsg: string;
  createdAt: string;
}

/** 敏感操作 */
export interface SensitiveOperation {
  id: string;
  operationName: string;
  operator: string;
  roleName: string;
  timestamp: string;
  resource: string;
  ip: string;
  result: 'success' | 'failure';
  details: string;
  riskLevel: 'high' | 'critical';
}

/** 权限矩阵行 */
export interface PermissionMatrixRow {
  permission: string;
  permissionGroup: string;
  roles: Record<string, boolean>;
}

// ===== G04 系统配置与数据字典 =====

/** CPC 控制点枚举 */
export type ControlPointLevel = 'CP-1' | 'CP-2' | '';

/** 10 节点 SLA 配置 */
export interface SlaNode {
  nodeId: number;
  nodeCode: string;
  nodeName: string;
  slaHours: number;
  timeoutAction: string;
  role: string;
  controlPoint: ControlPointLevel;
  isHardIntercept: boolean;
}

/** 温度阈值区间 */
export interface ThresholdRange {
  min: number;
  max: number;
  label: string;
}

/** 温控阈值配置（每种品类） */
export interface TempThreshold {
  productId: string;
  productName: string;
  target: ThresholdRange;
  warning: ThresholdRange;
  alarm: ThresholdRange;
}

/** 币种汇率配置 */
export interface CurrencyConfig {
  code: string;
  name: string;
  rate: number;
  updatedAt: string;
  syncFrequency: string;
}

/** 港口航线配置 */
export interface RouteConfig {
  id: string;
  origin: string;
  destination: string;
  sailingDays: number;
  carrier: string;
}

/** 模板状态 */
export type TemplateStatus = 'configured' | 'pending';

/** 模板管理配置 */
export interface TemplateConfig {
  id: string;
  name: string;
  status: TemplateStatus;
  updatedAt: string;
  updatedBy: string;
}

// ===== G01 AI Copilot 与公共验真 =====

import type { LiabilitySegment } from './order';

/** 对话消息类型枚举 */
export enum ChatMessageType {
  MORNING_REPORT = 'morning_report',
  USER_REQUEST = 'user_request',
  EVIDENCE_REPORT = 'evidence_report',
  SYSTEM_WARNING = 'system_warning',
}

/** AI 能力状态枚举 */
export enum AICapabilityStatus {
  ACTIVE = 'active',
  DEVELOPING = 'developing',
  PLANNED = 'planned',
}

/** HITL 优先级枚举 */
export enum HITLPriority {
  HIGH = 'high',
  CRITICAL = 'critical',
}

/** 定责分段（复用 order.ts 中定义，此处只做引用说明） */
// LiabilitySegment 已在 order.ts 中定义，请直接导入使用

/** 证据链报告元数据 */
export interface EvidenceReportMetadata {
  iotDataSummary: string;
  liabilityProgress: number;
  liabilitySegments: LiabilitySegment[];
  requiresManualReview: boolean;
  manualReviewReasons: string[];
  downloadableReportUrl: string;
  ticketTransferUrl: string;
}

/** AI 对话消息 */
export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  messageType: ChatMessageType;
  content: string;
  timestamp: string;
  metadata?: EvidenceReportMetadata;
}

/** AI 能力项 */
export interface AICapability {
  id: string;
  name: string;
  description: string;
  status: AICapabilityStatus;
  icon: string;
}

/** AI 调用统计 */
export interface AIStats {
  totalCalls: number;
  evidenceChainsGenerated: number;
  claimSuggestions: number;
  adoptionRate: number;
  avgResponseTime: number;
  uptimeDays: number;
}

/** HITL 强制人工复核规则 */
export interface HITLRule {
  id: string;
  category: string;
  description: string;
  trigger: string;
  priority: HITLPriority;
}

/** Token 链接 */
export interface TokenLink {
  id: string;
  url: string;
  label: string;
  expiryDate: string;
  accessCount: number;
  maxAccess: number;
  isValid: boolean;
}

/** 公共验真数据 */
export interface PublicVerification {
  hash: string;
  hashAlgorithm: string;
  qrCodeData: string;
  verificationCount: number;
  lastVerified: string;
  description: string;
}

// ===== G01 AI Copilot 辅助类型（仅供页面使用，不持久化） =====

/** QR 码验证请求（未来 API 对接用） */
export interface QRVerificationRequest {
  hash: string;
  timestamp: string;
}

/** QR 码验证响应 */
export interface QRVerificationResponse {
  valid: boolean;
  hash: string;
  verificationCount: number;
  lastVerified: string;
  message: string;
}

/** AI Copilot Token 操作结果 */
export interface CopilotTokenAction {
  action: 'copy' | 'invalidate' | 'create';
  tokenId: string;
  success: boolean;
  message: string;
}

// ===== P2 多租户自助开通与管理（T06/T07）=====

/** 租户状态：0-禁用 1-启用 2-归档 */
export type TenantStatus = 0 | 1 | 2;

/** 租户列表项（对齐 TenantListItemVO） */
export interface TenantListItem {
  id: string;
  orgCode: string;
  orgName: string;
  status: TenantStatus;
  parentId?: string | null;
  leader?: string;
  phone?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** 租户管理员账号概要（对齐 TenantDetailVO.TenantAdminAccountVO） */
export interface TenantAdminAccount {
  username: string;
  status: number;
  createdAt?: string;
}

/** 平台平面审计日志项（对齐 TenantDetailVO.TenantAuditItemVO，detail 为 JSON 文本） */
export interface TenantAuditItem {
  id: string;
  action: string;
  operatorId: string;
  operatorName: string;
  tenantCode: string;
  tenantName: string;
  detail: string;
  createdAt?: string;
}

/** 租户详情（对齐 TenantDetailVO） */
export interface TenantDetail {
  id: string;
  orgCode: string;
  orgName: string;
  status: TenantStatus;
  leader?: string;
  phone?: string;
  email?: string;
  remark?: string;
  createdAt?: string;
  updatedAt?: string;
  adminAccount?: TenantAdminAccount | null;
  auditLogs?: TenantAuditItem[];
}

/** 开通租户请求（对齐 CreateTenantReq） */
export interface CreateTenantReq {
  orgCode: string;
  orgName: string;
  leader?: string;
  phone?: string;
  email?: string;
  remark?: string;
}

/** 开通租户响应（含一次性明文初始密码，对齐 CreateTenantResp） */
export interface CreateTenantResp {
  orgCode: string;
  orgName: string;
  orgId: string;
  adminUsername: string;
  /** 初始密码（明文，仅随创建响应返回一次） */
  initialPassword: string;
  status: string;
}
